import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useClinicData } from "@/hooks/useClinicData";
import { CreateClinicForm } from "./CreateClinicForm";
import { JoinClinicForm } from "./JoinClinicForm";
import { InvitationAcceptance } from "./InvitationAcceptance";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Users, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export function ClinicAssociationFlow() {
  const { profile, user, refreshProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState<'choice' | 'create' | 'join' | 'invitation'>('choice');
  const [invitationToken, setInvitationToken] = useState<string | null>(null);

  // Check if user came from an invitation link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('invitation');
    if (token) {
      setInvitationToken(token);
      setCurrentStep('invitation');
    }
  }, []);

  // If user already has a clinic association, don't show this flow
  if (profile?.clinic_id) {
    return null;
  }

  // Only show for doctors, admin, and staff roles
  if (!profile?.role || !['doctor', 'admin', 'staff'].includes(profile.role)) {
    return null;
  }

  const handleSuccess = async () => {
    // After successful clinic association, update the provider record if the user is a doctor
    if (profile?.role === 'doctor') {
      try {
        // Get the updated profile to get the new clinic_id
        const { data: updatedProfile, error: profileError } = await supabase
          .from('profiles')
          .select('id, clinic_id')
          .eq('id', profile.id)
          .single();

        if (profileError || !updatedProfile) {
          throw new Error('Failed to fetch updated profile');
        }

        // Update the provider record with the new clinic_id
        const { error: providerError } = await supabase
          .from('providers')
          .update({ clinic_id: updatedProfile.clinic_id })
          .eq('profile_id', profile.id);

        if (providerError) {
          throw new Error('Failed to update provider record');
        }
      } catch (error) {
        console.error('Error updating provider record:', error);
      }
    }

    await refreshProfile();
  };

  if (currentStep === 'invitation' && invitationToken) {
    return (
      <InvitationAcceptance 
        token={invitationToken} 
        onSuccess={handleSuccess}
        onCancel={() => setCurrentStep('choice')}
      />
    );
  }

  if (currentStep === 'create') {
    return (
      <CreateClinicForm 
        onSuccess={handleSuccess}
        onCancel={() => setCurrentStep('choice')}
      />
    );
  }

  if (currentStep === 'join') {
    return (
      <JoinClinicForm 
        onSuccess={handleSuccess}
        onCancel={() => setCurrentStep('choice')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Clinic Association Required</CardTitle>
          <CardDescription>
            As a {profile?.role}, you need to be associated with a clinic to access the platform.
            Please choose how you'd like to proceed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button
              variant="outline"
              className="h-24 flex flex-col gap-2"
              onClick={() => setCurrentStep('create')}
            >
              <Building2 className="w-8 h-8 text-emerald-600" />
              <div>
                <div className="font-medium">Create New Clinic</div>
                <div className="text-sm text-gray-500">Start your own clinic</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="h-24 flex flex-col gap-2"
              onClick={() => setCurrentStep('join')}
            >
              <Users className="w-8 h-8 text-teal-600" />
              <div>
                <div className="font-medium">Join Existing Clinic</div>
                <div className="text-sm text-gray-500">Connect with an existing clinic</div>
              </div>
            </Button>
          </div>

          <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
            <div className="flex items-center gap-2 text-emerald-800 mb-2">
              <UserPlus className="w-5 h-5" />
              <span className="font-medium">Have an invitation?</span>
            </div>
            <p className="text-emerald-700 text-sm">
              If you received an invitation link via email, click on that link to automatically join the clinic.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
